import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import type { Recommendation, RecommendationsPageResponse } from '../lib/types'

/**
 * Manages the discovery-feed slices beyond the one embedded in the session
 * envelope (GET /sessions/:id/recommendations?after_rank=R — server-fixed
 * 20/slice). KEYSET pagination: each fetch resumes from the last rank the
 * client holds, so slices the server deepens in sub-page batches are never
 * skipped (page-number offsets were — the feed looked bottomed-out while
 * appended rows sat unserved). Thin like every other hook — ordering and
 * grounding stay server-side.
 */
export function useRecommendationFeed(
  sessionId: string | undefined,
  initialHasMore: boolean,
  initialAfterRank: number,
) {
  const [extra, setExtra] = useState<Recommendation[]>([])
  const [hasMore, setHasMore] = useState(initialHasMore)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [afterRank, setAfterRank] = useState(initialAfterRank)

  // Feed identity: a new session id (a re-roll navigates to a fresh session)
  // or a flipped embedded has_more (SSE/poll refresh on completion) starts the
  // feed over. Reset during render — the React "adjusting state when props
  // change" pattern — so no effect-driven setState is needed.
  const feedKey = `${sessionId ?? ''}::${initialHasMore}`
  const [prevFeedKey, setPrevFeedKey] = useState(feedKey)
  if (prevFeedKey !== feedKey) {
    setPrevFeedKey(feedKey)
    setExtra([])
    setHasMore(initialHasMore)
    setIsLoadingMore(false)
    setError(null)
    setAfterRank(initialAfterRank)
  }

  // Latest-key mirror so an in-flight fetch can detect a feed switch and a
  // stale response never leaks into the new feed.
  const feedKeyRef = useRef(feedKey)
  useEffect(() => {
    feedKeyRef.current = feedKey
  })
  // Which feed currently has a fetch in flight — guards the concurrent
  // double-fetch (the observer and the "Load more" button can both fire).
  const inFlightKeyRef = useRef<string | null>(null)

  const loadMore = useCallback(async () => {
    if (!sessionId || inFlightKeyRef.current === feedKey) return
    inFlightKeyRef.current = feedKey
    setIsLoadingMore(true)
    try {
      const data = await api.get<RecommendationsPageResponse>(
        `/sessions/${sessionId}/recommendations?after_rank=${afterRank}`,
      )
      if (feedKeyRef.current !== feedKey) return // feed switched mid-flight
      // Resume from wherever the server actually served to — a deepen round
      // can return fewer than a full slice without ending the feed.
      setAfterRank(data.next_after_rank ?? afterRank)
      setExtra((prev) => {
        // Dedupe by rec.id on append — SSE refreshes can race slice fetches.
        const seen = new Set(prev.map((r) => r.id))
        return [...prev, ...data.recommendations.filter((r) => !seen.has(r.id))]
      })
      setHasMore(data.has_more)
      setError(null)
    } catch (e) {
      if (feedKeyRef.current !== feedKey) return
      setError(e instanceof Error ? e.message : 'Could not load more picks.')
    } finally {
      if (inFlightKeyRef.current === feedKey) inFlightKeyRef.current = null
      if (feedKeyRef.current === feedKey) setIsLoadingMore(false)
    }
  }, [sessionId, feedKey, afterRank])

  return { extra, hasMore, isLoadingMore, error, loadMore }
}
